import React from 'react';
import {
  Alert,
  Button,
  Chip,
  Divider,
  Grid,
  Paper,
  Stack,
  Typography
} from '@mui/material';

import DownloadIcon from '@mui/icons-material/Download';
import toast from 'react-hot-toast';
import { useParameterizedQuery } from 'react-fetching-library';

import { TfgFeedLogs } from './TfgFeedLogs';
import { formatDateTime } from '../../../services/functions';
import { downloadFeedCSVAction } from '../../../services/request';

const Field = ({ label, value }) => (
  <Grid item xs={12} md={6}>
    <Typography
      variant="caption"
      color="text.secondary"
      sx={{ display: 'block' }}
    >
      {label}
    </Typography>

    <Typography variant="body1">
      {value || '-'}
    </Typography>
  </Grid>
);

const getStatusColor = status => {
  switch (status) {
  case 'PENDING':
    return 'warning';

  case 'PROCESSING':
    return 'info';

  case 'SENT':
    return 'primary';

  case 'ACCEPTED':
    return 'success';

  case 'REJECTED':
    return 'error';

  case 'ERROR':
    return 'error';

  default:
    return 'default';
  }
};

export const TfgFeedDetails = ({ rowData }) => {
  const { query } = useParameterizedQuery(downloadFeedCSVAction);

  const handleDownload = async () => {
    const { payload, error } = await query(rowData.id);

    if (error) {
      toast.error('Nie udało się pobrać pliku');

      return;
    }

    const url = window.URL.createObjectURL(payload);
    const a = document.createElement('a');
    document.body.appendChild(a);

    a.style.display = 'none';
    a.href = url;
    a.download = `Zasilenie TFG ${rowData.agreement} ${new Date().getMilliseconds()}`;

    a.click();

    window.URL.revokeObjectURL(url);
    a.remove();
  };

  if (!rowData) {
    return null;
  }

  return (
    <Stack spacing={2}>

      <Paper sx={{ p: 2 }}>

        <Stack spacing={2}>

          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="flex-start"
          >

            <Stack spacing={0.5}>
              <Typography variant="h5">
                Zasilenie TFG #{rowData.id}
              </Typography>

              <Typography
                variant="body2"
                color="text.secondary"
              >
                {rowData.agreement}
              </Typography>
            </Stack>

            <Stack
              direction="row"
              spacing={2}
              alignItems="center"
            >

              {rowData.feed_reference_number && (
                <Chip
                  label={`Nr ref. ${rowData.feed_reference_number}`}
                  variant="outlined"
                />
              )}

              {rowData.csv_file && (
                <Button
                  variant="outlined"
                  startIcon={<DownloadIcon />}
                  onClick={handleDownload}
                >
                  Pobierz Plik JSON
                </Button>
              )}

            </Stack>

          </Stack>

          <Divider />

          <Stack
            direction="row"
            spacing={4}
          >

            <Stack spacing={0.5}>
              <Typography
                pl={2}
                variant="caption"
                color="text.secondary"
              >
                Status
              </Typography>

              <Chip
                size="small"
                color={getStatusColor(rowData.status)}
                label={rowData.status_display}
              />
            </Stack>

            <Stack spacing={0.5}>
              <Typography
                pl={1}
                variant="caption"
                color="text.secondary"
              >
                Typ operacji
              </Typography>

              <Chip
                size="small"
                variant="outlined"
                label={rowData.operation_type_display}
              />
            </Stack>

            {rowData.operation_type === 'KOREKTA' && (
              <Stack spacing={0.5}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                >
                  Powód korekty
                </Typography>

                <Chip
                  size="small"
                  variant="outlined"
                  label={rowData.correction_reason || '-'}
                />
              </Stack>
            )}

          </Stack>

          <Divider />

          <Typography variant="subtitle1">
            Podstawowe informacje
          </Typography>

          <Grid container spacing={3}>
            <Field
              label="Umowa"
              value={rowData.agreement}
            />

            <Field
              label="Zamówienie"
              value={rowData.order}
            />
          </Grid>

          <Divider />

          <Typography variant="subtitle1">
            Harmonogram
          </Typography>

          <Grid container spacing={3}>
            <Field
              label="Zaplanowano wysyłkę"
              value={formatDateTime(rowData.scheduled_at)}
            />

            <Field
              label="Termin wysyłki"
              value={formatDateTime(rowData.deadline_at)}
            />

            <Field
              label="Przetworzono"
              value={
                rowData.processed_at
                  ? formatDateTime(rowData.processed_at)
                  : 'Jeszcze nie'
              }
            />
          </Grid>

          <Divider />

          <Typography variant="subtitle1">
            Historia wysyłki
          </Typography>

          <Grid container spacing={3}>
            <Field
              label="Liczba prób"
              value={rowData.attempts}
            />

            <Field
              label="Ostatnia próba"
              value={formatDateTime(rowData.last_attempt_at)}
            />

            <Field
              label="Utworzono"
              value={formatDateTime(rowData.created_at)}
            />
          </Grid>

          {rowData.error && (
            <>
              <Divider />

              <Alert severity="error">
                <Typography fontWeight={600}>
                  Ostatni błąd
                </Typography>

                <Typography variant="body2">
                  {rowData.error}
                </Typography>
              </Alert>
            </>
          )}

        </Stack>

      </Paper>

      <TfgFeedLogs logs={rowData.logs} />

    </Stack>
  );
};
